import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { ArrowRight, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MarketingLayout, SectionHeader, I18nPageHero } from "@/components/marketing/Layout";

export const Route = createFileRoute("/invest")({
  head: () => buildSeo({
    path: "/invest",
    title: "Invest & Partner — Reality Twin Network",
    description: "Reality Twin Network is actively raising capital and looking for strategic partners to bring an intelligent digital twin to every company.",
  }),
  component: InvestPage,
});

function InvestPage() {
  const { t } = useTranslation();
  const useFunds = t("invest.raise.use", { returnObjects: true }) as string[];
  const seek = t("invest.partners.seek", { returnObjects: true }) as string[];

  return (
    <MarketingLayout>
      <I18nPageHero tKey="pages.invest" />

      <section className="border-b border-border py-16">
        <div className="mx-auto max-w-3xl px-6 space-y-5 text-[16px] leading-[1.7] text-muted-foreground">
          <p>{t("invest.intro.p1")}</p>
          <p>{t("invest.intro.p2")}</p>
          <p className="text-foreground">{t("invest.intro.p3")}</p>
        </div>
      </section>

      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-6xl px-6 grid lg:grid-cols-[1fr_1fr] gap-12 items-start">
          <SectionHeader
            eyebrow={t("invest.raise.eyebrow")}
            title={<>{t("invest.raise.titleA")} <em className="italic text-muted-foreground">{t("invest.raise.titleEm")}</em></>}
            sub={t("invest.raise.body")}
          />
          <div className="rounded-xl hairline bg-card p-7">
            <div className="font-mono-eyebrow">{t("invest.raise.useTitle")}</div>
            <ul className="mt-4 space-y-3 text-[15px] leading-[1.6] text-muted-foreground">
              {useFunds.map((u, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-foreground/60" />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("invest.partners.eyebrow")}
            title={<>{t("invest.partners.titleA")} <em className="italic text-muted-foreground">{t("invest.partners.titleEm")}</em></>}
            sub={t("invest.partners.body")}
          />
          <div className="mt-10">
            <div className="font-mono-eyebrow">{t("invest.partners.seekTitle")}</div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border hairline rounded-xl overflow-hidden">
              {seek.map((s, i) => (
                <div key={i} className="bg-card p-6 text-[15px] leading-[1.55] text-foreground">
                  <span className="font-mono-eyebrow mr-2 text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                  {s}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <SectionHeader
            align="center"
            eyebrow={t("invest.vision.eyebrow")}
            title={<>{t("invest.vision.titleA")} <em className="italic text-muted-foreground">{t("invest.vision.titleEm")}</em></>}
            sub={t("invest.vision.body")}
          />
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-[40px] sm:text-[56px] leading-[1.02] tracking-[-0.025em]">
            {t("invest.cta.titleA")} <em className="italic text-muted-foreground">{t("invest.cta.titleEm")}</em>
          </h2>
          <p className="mt-5 text-[17px] leading-[1.55] text-muted-foreground">{t("invest.cta.body")}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition">
              {t("invest.cta.primary")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <a href="mailto:hello@realitytwin.io" className="inline-flex items-center gap-1.5 rounded-full hairline px-5 py-3 text-[14px] font-medium text-foreground hover:bg-cream transition">
              <Mail className="h-3.5 w-3.5" /> {t("invest.cta.secondary")}
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
